import { DeleteOutlined, LockOutlined, PlusOutlined } from '@ant-design/icons';
import {
  type BillingMethod,
  billsCount,
  type ConfigForm,
  checkForm,
  currentMonth,
  describeError,
  describeFee,
  describeShart,
  describeTier,
  dirhams,
  type FormFee,
  type FormTier,
  formFrom,
  fullDate,
  type MonthKey,
  monthLabel,
  PRICING_ERRORS,
  present,
  SAMPLE_CONSUMPTIONS,
  STATUS_LABELS,
  tierStarts,
  toApiInput,
} from '@horof/shared';
import {
  Alert,
  App,
  Button,
  Checkbox,
  DatePicker,
  Input,
  Modal,
  Radio,
  Skeleton,
} from 'antd';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from 'urql';
import { DeskPanel, useDirty, useSplit } from './panel';
import { PageHeader, Section, Status, type Tone } from './ui';
import {
  BillingPreviewDocument,
  type Config,
  DeleteBillingConfigurationDocument,
  PricingDocument,
  pricingContext,
  RenameBillingConfigurationDocument,
  SaveBillingConfigurationDocument,
  useIsCommittee,
} from './water-data';

const STATUS_TONES: Record<Config['status'], Tone> = {
  SCHEDULED: 'info',
  CURRENT: 'success',
  PAST: 'stopped',
};

/**
 * «الأسعار»: every price set with its months, what a member pays for usual
 * consumptions under each, and new prices from a month on (computed by
 * arqam before saving). Prices never change in place.
 */
export function PricesScreen() {
  const canEdit = useIsCommittee();
  const { modal, message } = App.useApp();
  const split = useSplit();
  const [{ data, error }] = useQuery({
    query: PricingDocument,
    variables: { consumptions: SAMPLE_CONSUMPTIONS },
    context: useMemo(() => pricingContext, []),
  });
  const [, remove] = useMutation(DeleteBillingConfigurationDocument);
  const [renaming, setRenaming] = useState<Config | null>(null);
  const configs = present(data?.billingConfigurations?.nodes);
  // The comparison: what is or will be used, and the set before.
  const compared = [
    ...configs.filter((c) => c.status !== 'PAST'),
    ...configs.filter((c) => c.status === 'PAST').slice(0, 1),
  ].reverse();

  async function confirmRemove(c: Config) {
    const ok = await modal.confirm({
      title: `حذف «${c.name}»؟`,
      content: 'لم تصدر بها أي فاتورة. تعود الأسعار السابقة سارية بعد حذفها.',
      okText: 'احذفها',
      okButtonProps: { danger: true },
      cancelText: 'رجوع',
    });
    if (!ok) return;
    const r = await remove({ configId: c.id }, pricingContext);
    if (r.error) {
      message.error(describeError(r.error, 'تعذّر الحذف.', PRICING_ERRORS));
    } else {
      message.success('حُذفت التسعيرة');
    }
  }

  return (
    <div className="desk-split">
      <section className="desk-page desk-summary">
        <PageHeader
          title="الأسعار"
          description="كل تسعيرة بأشهرها. تسعيرة صدرت بها فواتير لا تتغير أسعارها: تُضاف تسعيرة جديدة."
          actions={
            canEdit && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => split.open('new')}
              >
                أسعار جديدة
              </Button>
            )
          }
        />
        {error && <Alert type="error" showIcon message="تعذّر تحميل الأسعار" />}
        {!data && !error && <Skeleton active paragraph={{ rows: 8 }} />}

        {compared.length > 0 && (
          <Section
            title="ماذا يؤدي العضو؟"
            description="حسب الاستهلاك، بدون الشرط."
          >
            <table className="desk-mini-table">
              <thead>
                <tr>
                  <th>الاستهلاك</th>
                  {compared.map((c) => (
                    <th key={c.id}>
                      {c.name}{' '}
                      <Status tone={STATUS_TONES[c.status]}>
                        {STATUS_LABELS[c.status]}
                      </Status>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {SAMPLE_CONSUMPTIONS.map((m3) => (
                  <tr key={m3}>
                    <td className="ltr-num">{m3} م³</td>
                    {compared.map((c) => (
                      <td key={c.id}>
                        {dirhams(
                          c.preview.find((l) => l.consumption === m3)?.total,
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>
        )}

        <div className="desk-summary-grid">
          {configs.map((c, index) => {
            const tiers = present(c.consumptionTiers.nodes);
            const fees = present(c.feeConfigurations.nodes);
            const deletable = canEdit && index === 0 && !c.inUse;
            return (
              <Section
                key={c.id}
                title={
                  <>
                    {c.name}
                    <Status tone={STATUS_TONES[c.status]}>
                      {STATUS_LABELS[c.status]}
                    </Status>
                  </>
                }
                description={`من ${fullDate(c.effectiveFrom)}${c.effectiveTo ? ` إلى ${fullDate(c.effectiveTo)}` : ''}${c.bills.totalCount > 0 ? ` · ${billsCount(c.bills.totalCount)}` : ''}`}
                actions={
                  canEdit && (
                    <>
                      <Button onClick={() => setRenaming(c)}>
                        تغيير الاسم
                      </Button>
                      {deletable && (
                        <Button danger onClick={() => confirmRemove(c)}>
                          حذف
                        </Button>
                      )}
                    </>
                  )
                }
              >
                {c.description && <div>{c.description}</div>}
                <ul className="desk-facts">
                  {tiers.map((t) => (
                    <li key={t.id}>{describeTier(t, c.billingMethod)}</li>
                  ))}
                  {fees.map((f) => (
                    <li key={f.id}>{describeFee(f)}</li>
                  ))}
                  <li>{describeShart(c.shartUnitPrice)}</li>
                </ul>
                {c.inUse && (
                  <div className="field-hint">
                    <LockOutlined /> صدرت بها فواتير: لا تتغير أسعارها.
                  </div>
                )}
              </Section>
            );
          })}
        </div>
        <div className="field-hint">
          يُضاف إلى كل فاتورة ثمن «الشرط» حسب عدد الأفراد المسجَّلين فيه.
        </div>
      </section>

      {split.panel === 'new' && data && (
        <NewPricesPanel
          configs={configs}
          onClose={split.close}
          onDirtyChange={split.onDirtyChange}
        />
      )}
      {renaming && (
        <RenameDialog config={renaming} onClose={() => setRenaming(null)} />
      )}
    </div>
  );
}

const emptyTier: FormTier = { end: '', rate: '', description: '' };
const emptyFee: FormFee = {
  name: '',
  feeType: 'FIXED',
  amount: '',
  onlyWhenNoConsumption: false,
  description: '',
};

/** The day before a "YYYY-MM-01": when the previous prices end. */
const dayBefore = (date: string) => {
  const d = new Date(`${date}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() - 1);
  return d.toISOString().slice(0, 10);
};

function useDebounced<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(timer);
  }, [value, ms]);
  return debounced;
}

/** New prices from a month on, starting from the latest set's. */
function NewPricesPanel({
  configs,
  onClose,
  onDirtyChange,
}: {
  configs: Config[];
  onClose: () => void;
  onDirtyChange: (dirty: boolean) => void;
}) {
  const { modal, message } = App.useApp();
  const latest = configs[0] ?? null;
  const current = configs.find((c) => c.status === 'CURRENT') ?? null;
  const [initial] = useState(() =>
    formFrom(
      latest && {
        billingMethod: latest.billingMethod,
        effectiveFrom: latest.effectiveFrom,
        tiers: present(latest.consumptionTiers.nodes),
        fees: present(latest.feeConfigurations.nodes),
        shartUnitPrice: latest.shartUnitPrice,
      },
      currentMonth(),
      current?.shartUnitPrice,
    ),
  );
  const [form, setForm] = useState<ConfigForm>(initial);
  const [submitted, setSubmitted] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [{ fetching: saving }, save] = useMutation(
    SaveBillingConfigurationDocument,
  );
  useDirty(form, initial, onDirtyChange);

  const problems = checkForm(form);
  const problemAt = (path: string) =>
    problems.find((p) => p.path === path)?.message;
  const update = (patch: Partial<ConfigForm>) =>
    setForm((f) => ({ ...f, ...patch }));
  const setTier = (i: number, patch: Partial<FormTier>) =>
    update({
      tiers: form.tiers.map((t, j) => (j === i ? { ...t, ...patch } : t)),
    });
  const setFee = (i: number, patch: Partial<FormFee>) =>
    update({
      fees: form.fees.map((f, j) => (j === i ? { ...f, ...patch } : f)),
    });

  // arqam prices the sample bills with the unsaved prices.
  const priceProblems = problems.filter((p) => p.path !== 'name');
  const pricing = useDebounced(
    useMemo(() => {
      const input = toApiInput(form);
      return {
        billingMethod: input.billingMethod,
        tiers: input.tiers,
        fees: input.fees,
        consumptions: SAMPLE_CONSUMPTIONS,
      };
    }, [form]),
    400,
  );
  const [preview] = useQuery({
    query: BillingPreviewDocument,
    variables: pricing,
    pause: priceProblems.length > 0,
    requestPolicy: 'network-only',
  });
  const currentTotals = Object.fromEntries(
    (current?.preview ?? []).map((l) => [l.consumption, l.total]),
  );

  const fixed = form.billingMethod === 'FIXED';
  const tiers = fixed ? form.tiers.slice(0, 1) : form.tiers;
  const starts = tierStarts(tiers);
  const effectiveFrom = `${form.month}-01`;

  async function submit() {
    setSubmitted(true);
    setProblem(null);
    if (problems.length) return;
    const ok = await modal.confirm({
      title: 'حفظ الأسعار الجديدة',
      width: 520,
      content: (
        <>
          <p>
            ابتداءً من <strong>{fullDate(effectiveFrom)}</strong> تُحسب الفواتير
            الجديدة بهذه الأسعار.
          </p>
          {latest && (
            <p>
              تنتهي «{latest.name}» في {fullDate(dayBefore(effectiveFrom))}.
            </p>
          )}
          <p>الشرط: {dirhams(toApiInput(form).shartUnitPrice)} للفرد شهرياً.</p>
          <p>لا يمكن تغيير الأسعار بعد صدور فواتير بها.</p>
        </>
      ),
      okText: 'حفظ',
      cancelText: 'رجوع',
    });
    if (!ok) return;
    const r = await save({ input: toApiInput(form) }, pricingContext);
    if (r.error || !r.data?.appSaveBillingConfiguration?.result) {
      setProblem(describeError(r.error, 'تعذّر حفظ الأسعار.', PRICING_ERRORS));
      return;
    }
    message.success('حُفظت الأسعار الجديدة');
    onDirtyChange(false);
    onClose();
  }

  return (
    <DeskPanel title="أسعار جديدة" onClose={onClose} onSave={submit}>
      {problem && <Alert type="error" showIcon message={problem} />}

      <section className="desk-panel-section">
        <div className="desk-fields">
          <label htmlFor="price-name">الاسم</label>
          <label htmlFor="price-month">تبدأ في شهر</label>
          <span>
            <Input
              id="price-name"
              maxLength={255}
              placeholder={`مثلاً «أسعار ${monthLabel(form.month)}»`}
              value={form.name}
              status={submitted && problemAt('name') ? 'error' : undefined}
              onChange={(e) => update({ name: e.target.value })}
            />
            {submitted && problemAt('name') && (
              <span className="field-error">{problemAt('name')}</span>
            )}
          </span>
          <DatePicker
            id="price-month"
            picker="month"
            allowClear={false}
            format="MMMM YYYY"
            value={dayjs(`${form.month}-01`)}
            disabledDate={(d) => d.isBefore(dayjs().startOf('month'))}
            onChange={(d) =>
              d && update({ month: d.format('YYYY-MM') as MonthKey })
            }
          />
        </div>
        <div className="field-hint">
          من {fullDate(effectiveFrom)}
          {latest &&
            `. تنتهي «${latest.name}» في ${fullDate(dayBefore(effectiveFrom))}`}
          .
        </div>
        <Input
          placeholder="ملاحظة (اختيارية)"
          value={form.description}
          onChange={(e) => update({ description: e.target.value })}
        />
      </section>

      <section className="desk-panel-section">
        <h3>ثمن الاستهلاك</h3>
        <Radio.Group
          optionType="button"
          buttonStyle="solid"
          value={form.billingMethod}
          onChange={(e) =>
            update({ billingMethod: e.target.value as BillingMethod })
          }
          options={[
            { value: 'PROGRESSIVE', label: 'بالشرائح' },
            { value: 'FIXED', label: 'سعر موحّد' },
          ]}
        />
        <table className="desk-mini-table desk-edit-table">
          <thead>
            <tr>
              {!fixed && <th>من (م³)</th>}
              {!fixed && <th>إلى (م³)</th>}
              <th>درهم / م³</th>
              {!fixed && <th>الاسم على الفاتورة</th>}
              <th />
            </tr>
          </thead>
          <tbody>
            {tiers.map((tier, i) => {
              const last = i === tiers.length - 1;
              return (
                // biome-ignore lint/suspicious/noArrayIndexKey: tiers are positional
                <tr key={i} className="desk-edit-row">
                  {!fixed && <td className="ltr-num">{starts[i] ?? '…'}</td>}
                  {!fixed && (
                    <td>
                      {last ? (
                        <span className="field-hint">فما فوق</span>
                      ) : (
                        <Input
                          size="small"
                          className="ltr-num"
                          aria-label={`نهاية الشريحة ${i + 1}`}
                          value={tier.end}
                          status={
                            problemAt(`tiers.${i}.end`) ? 'error' : undefined
                          }
                          title={problemAt(`tiers.${i}.end`)}
                          onChange={(e) => setTier(i, { end: e.target.value })}
                        />
                      )}
                    </td>
                  )}
                  <td>
                    <Input
                      size="small"
                      className="ltr-num"
                      aria-label={`سعر الشريحة ${i + 1}`}
                      value={tier.rate}
                      status={
                        problemAt(`tiers.${i}.rate`) ? 'error' : undefined
                      }
                      title={problemAt(`tiers.${i}.rate`)}
                      onChange={(e) => setTier(i, { rate: e.target.value })}
                    />
                  </td>
                  {!fixed && (
                    <td>
                      <Input
                        size="small"
                        aria-label={`اسم الشريحة ${i + 1}`}
                        value={tier.description}
                        onChange={(e) =>
                          setTier(i, { description: e.target.value })
                        }
                      />
                    </td>
                  )}
                  <td>
                    {!fixed && tiers.length > 1 && (
                      <Button
                        size="small"
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        aria-label="حذف الشريحة"
                        onClick={() =>
                          update({
                            tiers: form.tiers.filter((_, j) => j !== i),
                          })
                        }
                      />
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {!fixed && (
          <Button
            size="small"
            icon={<PlusOutlined />}
            onClick={() => update({ tiers: [...form.tiers, emptyTier] })}
          >
            إضافة شريحة
          </Button>
        )}
      </section>

      <section className="desk-panel-section">
        <h3>الرسوم</h3>
        {form.fees.length === 0 && <div className="field-hint">لا رسوم.</div>}
        {form.fees.map((fee, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: fees are positional
          <div key={i} className="desk-fee-row">
            <Input
              size="small"
              placeholder="اسم الرسم"
              value={fee.name}
              status={problemAt(`fees.${i}.name`) ? 'error' : undefined}
              title={problemAt(`fees.${i}.name`)}
              onChange={(e) => setFee(i, { name: e.target.value })}
            />
            <Radio.Group
              size="small"
              optionType="button"
              value={fee.feeType}
              onChange={(e) => setFee(i, { feeType: e.target.value })}
              options={[
                { value: 'FIXED', label: 'درهم' },
                { value: 'PERCENTAGE', label: '%' },
              ]}
            />
            <Input
              size="small"
              className="ltr-num desk-fee-amount"
              value={fee.amount}
              status={problemAt(`fees.${i}.amount`) ? 'error' : undefined}
              title={problemAt(`fees.${i}.amount`)}
              onChange={(e) => setFee(i, { amount: e.target.value })}
            />
            <Checkbox
              checked={fee.onlyWhenNoConsumption}
              onChange={(e) =>
                setFee(i, { onlyWhenNoConsumption: e.target.checked })
              }
            >
              عند استهلاك منعدم فقط
            </Checkbox>
            <Button
              size="small"
              type="text"
              danger
              icon={<DeleteOutlined />}
              aria-label="حذف الرسم"
              onClick={() =>
                update({ fees: form.fees.filter((_, j) => j !== i) })
              }
            />
          </div>
        ))}
        <Button
          size="small"
          icon={<PlusOutlined />}
          onClick={() => update({ fees: [...form.fees, emptyFee] })}
        >
          إضافة رسم
        </Button>
      </section>

      <section className="desk-panel-section">
        <h3>الشرط</h3>
        <div className="desk-inline-field">
          <label htmlFor="shart">ثمن الشرط للفرد شهرياً (درهم)</label>
          <Input
            id="shart"
            className="ltr-num"
            value={form.shartUnitPrice}
            status={problemAt('shartUnitPrice') ? 'error' : undefined}
            onChange={(e) => update({ shartUnitPrice: e.target.value })}
          />
        </div>
        {problemAt('shartUnitPrice') && (
          <div className="field-error">{problemAt('shartUnitPrice')}</div>
        )}
      </section>

      <section className="desk-panel-section">
        <h3>ماذا سيؤدي العضو؟ (بدون الشرط)</h3>
        {priceProblems.length > 0 ? (
          <div className="field-error">
            {priceProblems[0].message} (صحّح الحقول المعلَّمة بالأحمر.)
          </div>
        ) : preview.error ? (
          <Alert
            type="warning"
            showIcon
            message={describeError(
              preview.error,
              'تعذّر حساب المقارنة.',
              PRICING_ERRORS,
            )}
          />
        ) : (
          <table className="desk-mini-table">
            <thead>
              <tr>
                <th>الاستهلاك</th>
                <th>الأسعار الحالية</th>
                <th>الأسعار الجديدة</th>
              </tr>
            </thead>
            <tbody>
              {SAMPLE_CONSUMPTIONS.map((c) => {
                const line = preview.data?.billingPreview.find(
                  (l) => l.consumption === c,
                );
                return (
                  <tr key={c}>
                    <td className="ltr-num">{c} م³</td>
                    <td>
                      {currentTotals[c] ? dirhams(currentTotals[c]) : '—'}
                    </td>
                    <td className="is-after">
                      {line && !preview.stale ? dirhams(line.total) : '…'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </section>

      {submitted && problems.length > 0 && (
        <Alert type="error" showIcon message="صحّح الحقول المعلَّمة بالأحمر." />
      )}
      <div className="desk-actions">
        <Button type="primary" loading={saving} onClick={submit} title="Ctrl+S">
          حفظ الأسعار الجديدة
        </Button>
      </div>
    </DeskPanel>
  );
}

function RenameDialog({
  config,
  onClose,
}: {
  config: Config;
  onClose: () => void;
}) {
  const { message } = App.useApp();
  const [name, setName] = useState(config.name);
  const [description, setDescription] = useState(config.description ?? '');
  const [problem, setProblem] = useState<string | null>(null);
  const [{ fetching }, rename] = useMutation(
    RenameBillingConfigurationDocument,
  );

  async function save() {
    if (!name.trim()) {
      setProblem('اكتب اسماً.');
      return;
    }
    const r = await rename(
      {
        configId: config.id,
        name: name.trim(),
        description: description.trim() || null,
      },
      pricingContext,
    );
    if (r.error) {
      setProblem(describeError(r.error, 'تعذّر تغيير الاسم.', PRICING_ERRORS));
      return;
    }
    message.success('حُفظ الاسم');
    onClose();
  }

  return (
    <Modal
      open
      title="تغيير الاسم"
      okText="حفظ"
      cancelText="إلغاء"
      confirmLoading={fetching}
      onOk={save}
      onCancel={onClose}
    >
      <div className="desk-dialog">
        <Input
          maxLength={255}
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <Input
          placeholder="ملاحظة (اختيارية)"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
        {problem && <div className="field-error">{problem}</div>}
      </div>
    </Modal>
  );
}
